import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TruncatedText } from "components/shared/truncated-text";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type MultiSelectOption<T extends string | number> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export interface MultiSelectProps<T extends string | number> {
  options: MultiSelectOption<T>[];
  value: T[];
  onValueChange: (next: T[]) => void;
  triggerLabel: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  showClear?: boolean;
  className?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Render the popover as modal. Non-modal (default) preserves existing behaviour but does not
   * reliably dismiss when a Radix `Select` trigger is clicked (its pointer-down is defaultPrevented,
   * so the dismiss layer ignores it). Set this when the multi-select sits next to other Radix
   * dropdowns to guarantee only one is open at a time.
   */
  modal?: boolean;
}

export function MultiSelect<T extends string | number>({
  options,
  value,
  onValueChange,
  triggerLabel,
  searchable = false,
  searchPlaceholder = "Search…",
  emptyMessage = "No results found.",
  showClear = true,
  className,
  disabled,
  id,
  modal = false,
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(() => {
    if (!searchable || query.trim() === "") return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const toggle = (optionValue: T) => {
    if (value.includes(optionValue)) {
      onValueChange(value.filter((v) => v !== optionValue));
    } else {
      onValueChange([...value, optionValue]);
    }
  };

  return (
    <Popover
      modal={modal}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <TruncatedText as="span" className="min-w-0">
            {triggerLabel}
          </TruncatedText>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0 gap-0"
        align="start"
      >
        <div className="flex flex-col">
          {searchable && (
            <div className="border-b p-1">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8"
              />
            </div>
          )}
          <div className="max-h-72 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    disabled={option.disabled}
                    onClick={() => toggle(option.value)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer text-left disabled:pointer-events-none disabled:opacity-50",
                    )}
                  >
                    <TruncatedText as="span" className="max-w-full">
                      {option.label}
                    </TruncatedText>
                    {isSelected && <Check className="h-4 w-4 shrink-0 " />}
                  </button>
                );
              })
            )}
          </div>
          {showClear && value.length > 0 && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={() => onValueChange([])}
                className="w-full rounded-sm px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer text-left"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function formatMultiSelectLabel(
  selectedLabels: string[],
  fallback: string,
): string {
  if (selectedLabels.length === 0) return fallback;
  if (selectedLabels.length === 1) return selectedLabels[0];
  return `${selectedLabels.length} selected`;
}
